import { formatConfidencePercent } from './confidence';

export interface ProviderEdgePortSignalsRecord {
  hasPublicIp?: boolean;
  publicIpAddresses?: string[];
  bgpPeerCount?: number;
  ebgpPeerCount?: number;
  remoteAsns?: string[];
  hasVpnConfig?: boolean;
  vpnSessionCount?: number;
  transportSessionCount?: number;
  lldpNeighborCount?: number;
  cdpNeighborCount?: number;
  learnedMacCount?: number;
  arpEntryCount?: number;
  defaultRouteMatch?: boolean;
  hasNatOutside?: boolean;
  negativeAccessSignal?: boolean;
  isShutdown?: boolean;
}

export interface ProviderEdgeProviderIdentityRecord {
  providerName?: string | null;
  providerNormalizedName?: string | null;
  providerConfidence?: string | null;
  providerType?: string | null;
  providerEvidence?: string[] | null;
  circuitId?: string | null;
  bandwidthHint?: string | null;
}

export interface ProviderEdgeSiteAdjudicationInputSummaryRecord {
  candidateCount?: number | null;
  topologyEdgeCount?: number | null;
  topologyComplete?: boolean | null;
}

export interface ProviderEdgeSiteAdjudicationRecord {
  siteEdgeRole?: string | null;
  siteEdgeConfidence?: string | null;
  siteEdgeScore?: number | null;
  siteEdgeReasons?: string[] | null;
  siteScope?: string | null;
  upstreamProviderEdgeDeviceId?: string | null;
  upstreamProviderEdgeInterfaceName?: string | null;
  reconciliationVersion?: string | null;
  siteReconciledAt?: string | null;
  inputSummary?: ProviderEdgeSiteAdjudicationInputSummaryRecord | null;
}

export interface ProviderEdgeRedundancyRecord {
  redundancyGroupId?: string | null;
  uplinkRole?: string | null;
  pairedInterfaceName?: string | null;
  pairedDeviceId?: string | null;
  pairingConfidence?: string | null;
  pairingEvidence?: string[] | null;
}

export interface ProviderEdgePortRecord {
  deviceName?: string | null;
  interfaceName?: string | null;
  classification?: string | null;
  confidence?: number | null;
  reasons?: string[] | null;
  signals?: ProviderEdgePortSignalsRecord | null;
  providerIdentity?: ProviderEdgeProviderIdentityRecord | null;
  siteAdjudication?: ProviderEdgeSiteAdjudicationRecord | null;
  redundancy?: ProviderEdgeRedundancyRecord | null;
}

const readString = (value: unknown): string => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim());

const readNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(readString(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const readOptionalNumber = (value: unknown): number | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string' && value.trim().length === 0) {
    return null;
  }
  const parsed =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : Number(readString(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const readBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = readString(value).toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const readObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

export const normalizeProviderEdgePortRecord = (value: unknown): ProviderEdgePortRecord | null => {
  const record = readObject(value);
  if (!record) {
    return null;
  }
  const signals = readObject(record.signals);
  const rawPublicIpAddresses = signals ? signals.publicIpAddresses ?? signals.public_ip_addresses : undefined;
  const rawRemoteAsns = signals ? signals.remoteAsns ?? signals.remote_asns : undefined;
  const providerIdentity = readObject(record.providerIdentity ?? record.provider_identity);
  const siteAdjudication = readObject(record.siteAdjudication ?? record.site_adjudication);
  const redundancy = readObject(record.redundancy);
  const inputSummary = siteAdjudication ? readObject(siteAdjudication.inputSummary ?? siteAdjudication.input_summary) : null;
  const rawProviderEvidence = providerIdentity ? providerIdentity.providerEvidence ?? providerIdentity.provider_evidence : undefined;
  const rawSiteEdgeReasons = siteAdjudication ? siteAdjudication.siteEdgeReasons ?? siteAdjudication.site_edge_reasons : undefined;
  const rawPairingEvidence = redundancy ? redundancy.pairingEvidence ?? redundancy.pairing_evidence : undefined;
  return {
    deviceName: readString(record.deviceName ?? record.device_name),
    interfaceName: readString(record.interfaceName ?? record.interface_name),
    classification: readString(record.classification),
    confidence: readOptionalNumber(record.confidence),
    reasons: Array.isArray(record.reasons) ? record.reasons.map((item) => readString(item)).filter(Boolean) : [],
    signals: signals
        ? {
            hasPublicIp: readBoolean(signals.hasPublicIp ?? signals.has_public_ip),
          publicIpAddresses: Array.isArray(rawPublicIpAddresses)
            ? rawPublicIpAddresses.map((item: unknown) => readString(item)).filter(Boolean)
            : [],
          bgpPeerCount: readNumber(signals.bgpPeerCount ?? signals.bgp_peer_count),
          ebgpPeerCount: readNumber(signals.ebgpPeerCount ?? signals.ebgp_peer_count),
          remoteAsns: Array.isArray(rawRemoteAsns)
            ? rawRemoteAsns.map((item: unknown) => readString(item)).filter(Boolean)
            : [],
          hasVpnConfig: readBoolean(signals.hasVpnConfig ?? signals.has_vpn_config),
          vpnSessionCount: readNumber(signals.vpnSessionCount ?? signals.vpn_session_count),
          transportSessionCount: readNumber(signals.transportSessionCount ?? signals.transport_session_count),
          lldpNeighborCount: readNumber(signals.lldpNeighborCount ?? signals.lldp_neighbor_count),
          cdpNeighborCount: readNumber(signals.cdpNeighborCount ?? signals.cdp_neighbor_count),
          learnedMacCount: readNumber(signals.learnedMacCount ?? signals.learned_mac_count),
          arpEntryCount: readNumber(signals.arpEntryCount ?? signals.arp_entry_count),
          defaultRouteMatch: readBoolean(signals.defaultRouteMatch ?? signals.default_route_match),
          hasNatOutside: readBoolean(signals.hasNatOutside ?? signals.has_nat_outside),
          negativeAccessSignal: readBoolean(signals.negativeAccessSignal ?? signals.negative_access_signal),
          isShutdown: readBoolean(signals.isShutdown ?? signals.is_shutdown)
        }
      : null,
    providerIdentity: providerIdentity
      ? {
          providerName: readString(providerIdentity.providerName ?? providerIdentity.provider_name) || null,
          providerNormalizedName: readString(providerIdentity.providerNormalizedName ?? providerIdentity.provider_normalized_name) || null,
          providerConfidence: readString(providerIdentity.providerConfidence ?? providerIdentity.provider_confidence) || null,
          providerType: readString(providerIdentity.providerType ?? providerIdentity.provider_type) || null,
          providerEvidence: Array.isArray(rawProviderEvidence)
            ? rawProviderEvidence.map((item: unknown) => readString(item)).filter(Boolean)
            : [],
          circuitId: readString(providerIdentity.circuitId ?? providerIdentity.circuit_id) || null,
          bandwidthHint: readString(providerIdentity.bandwidthHint ?? providerIdentity.bandwidth_hint) || null
        }
      : null,
    siteAdjudication: siteAdjudication
      ? {
          siteEdgeRole: readString(siteAdjudication.siteEdgeRole ?? siteAdjudication.site_edge_role) || null,
          siteEdgeConfidence: readString(siteAdjudication.siteEdgeConfidence ?? siteAdjudication.site_edge_confidence) || null,
          siteEdgeScore: readOptionalNumber(siteAdjudication.siteEdgeScore ?? siteAdjudication.site_edge_score),
          siteEdgeReasons: Array.isArray(rawSiteEdgeReasons)
            ? rawSiteEdgeReasons.map((item: unknown) => readString(item)).filter(Boolean)
            : [],
          siteScope: readString(siteAdjudication.siteScope ?? siteAdjudication.site_scope) || null,
          upstreamProviderEdgeDeviceId: readString(siteAdjudication.upstreamProviderEdgeDeviceId ?? siteAdjudication.upstream_provider_edge_device_id) || null,
          upstreamProviderEdgeInterfaceName: readString(siteAdjudication.upstreamProviderEdgeInterfaceName ?? siteAdjudication.upstream_provider_edge_interface_name) || null,
          reconciliationVersion: readString(siteAdjudication.reconciliationVersion ?? siteAdjudication.reconciliation_version) || null,
          siteReconciledAt: readString(siteAdjudication.siteReconciledAt ?? siteAdjudication.site_reconciled_at) || null,
          inputSummary: inputSummary
            ? {
                candidateCount: readOptionalNumber(inputSummary.candidateCount ?? inputSummary.candidate_count),
                topologyEdgeCount: readOptionalNumber(inputSummary.topologyEdgeCount ?? inputSummary.topology_edge_count),
                topologyComplete: inputSummary.topologyComplete == null && inputSummary.topology_complete == null
                  ? null
                  : readBoolean(inputSummary.topologyComplete ?? inputSummary.topology_complete)
              }
            : null
        }
      : null,
    redundancy: redundancy
      ? {
          redundancyGroupId: readString(redundancy.redundancyGroupId ?? redundancy.redundancy_group_id) || null,
          uplinkRole: readString(redundancy.uplinkRole ?? redundancy.uplink_role) || null,
          pairedInterfaceName: readString(redundancy.pairedInterfaceName ?? redundancy.paired_interface_name) || null,
          pairedDeviceId: readString(redundancy.pairedDeviceId ?? redundancy.paired_device_id) || null,
          pairingConfidence: readString(redundancy.pairingConfidence ?? redundancy.pairing_confidence) || null,
          pairingEvidence: Array.isArray(rawPairingEvidence)
            ? rawPairingEvidence.map((item: unknown) => readString(item)).filter(Boolean)
            : []
        }
      : null
  };
};

export const normalizeProviderEdgePorts = (value: unknown): ProviderEdgePortRecord[] =>
  Array.isArray(value) ? (value.map(normalizeProviderEdgePortRecord).filter(Boolean) as ProviderEdgePortRecord[]) : [];

export const providerEdgeClassificationTone = (classification?: string | null): 'success' | 'warning' | 'ghost' => {
  const normalized = (classification ?? '').trim().toLowerCase();
  if (normalized.startsWith('confirmed') || normalized.startsWith('likely')) {
    return 'success';
  }
  if (normalized.includes('not_provider') || normalized.includes('suppressed')) {
    return 'ghost';
  }
  return normalized ? 'warning' : 'ghost';
};

export const shouldDisplayProviderEdgePort = (port?: ProviderEdgePortRecord | null): boolean => {
  if (!port) {
    return false;
  }
  const normalized = (port.classification ?? '').trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return !(normalized.includes('not_provider') || normalized.includes('suppressed'));
};

export const shouldDisplayProviderEdgeLink = (port?: ProviderEdgePortRecord | null): boolean => {
  if (!port) {
    return false;
  }
  const siteRole = (port.siteAdjudication?.siteEdgeRole ?? '').trim().toLowerCase();
  if (siteRole === 'provider_handoff') {
    return true;
  }
  const uplinkRole = (port.redundancy?.uplinkRole ?? '').trim().toLowerCase();
  if (uplinkRole === 'primary' || uplinkRole === 'standby' || uplinkRole === 'active_active' || uplinkRole === 'single') {
    return true;
  }
  const providerIdentityHint =
    (port.providerIdentity?.providerName ?? '').trim() ||
    (port.providerIdentity?.providerNormalizedName ?? '').trim() ||
    (port.providerIdentity?.providerType ?? '').trim();
  if (providerIdentityHint) {
    return shouldDisplayProviderEdgePort(port);
  }
  const classification = (port.classification ?? '').trim().toLowerCase();
  if (!classification) {
    return false;
  }
  if (classification.includes('insufficient') || classification.includes('not_provider') || classification.includes('suppressed')) {
    return false;
  }
  return true;
};

export const formatProviderEdgeLabel = (classification?: string | null): string => {
  const normalized = (classification ?? '').trim();
  if (!normalized) {
    return 'Unclassified';
  }
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const providerEdgeEffectiveRole = (port?: ProviderEdgePortRecord | null): string => {
  const siteRole = readString(port?.siteAdjudication?.siteEdgeRole);
  if (siteRole) {
    return siteRole;
  }
  return readString(port?.classification);
};

export const formatProviderEdgeConfidence = (confidence?: number | null): string => {
  return formatConfidencePercent(confidence);
};

export const describeProviderEdgeEvidence = (port?: ProviderEdgePortRecord | null): string => {
  if (!port) {
    return '—';
  }
  const siteReasons = Array.isArray(port.siteAdjudication?.siteEdgeReasons) ? port.siteAdjudication?.siteEdgeReasons?.filter(Boolean) : [];
  if ((siteReasons?.length ?? 0) > 0) {
    return siteReasons!.slice(0, 2).join(' · ');
  }
  const reasons = Array.isArray(port.reasons) ? port.reasons.filter(Boolean) : [];
  if (reasons.length > 0) {
    return reasons.slice(0, 2).join(' · ');
  }
  const providerEvidence = Array.isArray(port.providerIdentity?.providerEvidence) ? port.providerIdentity?.providerEvidence?.filter(Boolean) : [];
  if ((providerEvidence?.length ?? 0) > 0) {
    return providerEvidence!.slice(0, 2).join(' · ');
  }
  const signals = port.signals;
  if (!signals) {
    return '—';
  }
  const evidence: string[] = [];
  if ((signals.publicIpAddresses?.length ?? 0) > 0) {
    evidence.push(`Public IP ${signals.publicIpAddresses?.slice(0, 2).join(', ')}`);
  } else if (signals.hasPublicIp) {
    evidence.push('Public IP present');
  }
  if ((signals.remoteAsns?.length ?? 0) > 0) {
    evidence.push(`Remote AS ${signals.remoteAsns?.slice(0, 2).join(', ')}`);
  } else if ((signals.ebgpPeerCount ?? 0) > 0) {
    evidence.push(`eBGP peers ${signals.ebgpPeerCount}`);
  } else if ((signals.bgpPeerCount ?? 0) > 0) {
    evidence.push(`BGP peers ${signals.bgpPeerCount}`);
  }
  if (signals.defaultRouteMatch) {
    evidence.push('Default route match');
  }
  if (signals.hasVpnConfig || (signals.vpnSessionCount ?? 0) > 0) {
    evidence.push(`VPN ${(signals.vpnSessionCount ?? 0) > 0 ? `${signals.vpnSessionCount} sessions` : 'config'}`);
  }
  if ((signals.transportSessionCount ?? 0) > 0) {
    evidence.push(`Transport sessions ${signals.transportSessionCount}`);
  }
  if ((signals.lldpNeighborCount ?? 0) > 0 || (signals.cdpNeighborCount ?? 0) > 0) {
    evidence.push(`Neighbors ${(signals.lldpNeighborCount ?? 0) + (signals.cdpNeighborCount ?? 0)}`);
  }
  if ((signals.learnedMacCount ?? 0) > 0 || (signals.arpEntryCount ?? 0) > 0) {
    evidence.push(`MAC/ARP ${(signals.learnedMacCount ?? 0)}/${(signals.arpEntryCount ?? 0)}`);
  }
  if (signals.hasNatOutside) {
    evidence.push('NAT outside');
  }
  if (signals.negativeAccessSignal) {
    evidence.push('Access suppression signal');
  }
  if (signals.isShutdown) {
    evidence.push('Interface shutdown');
  }
  return evidence.length > 0 ? evidence.slice(0, 3).join(' · ') : '—';
};
